/**
 * Amazon Ads DSP fetchMetrics orchestration (Story 19.16, Task 8)
 *
 * The adapter's `fetchMetrics` dispatch routes DSP-only requests to
 * `runDspFetchMetrics`. The product-specific bits live here; the heavy
 * pipeline (submit / poll / download / parse) is in shared/report-orchestration.
 *
 * Per Story 19.16 AC #2 / #4 / #6 / #8 / #11:
 *   - 130 priority DSP metrics across DSP_CAMPAIGN / DSP_INVENTORY /
 *     DSP_GEOGRAPHY / DSP_PRODUCT
 *   - 31-day max date range per chunk (corrects integration-mapping
 *     directive's stale 60-day cache — verified May 2026)
 *   - 12h reporting delay → `dataFreshnessLagHours: 12`
 *   - 84h cache freshness window (12h delay + 48h finalization + 24h headroom)
 *   - 9-row in-flight safeguard via shared pipeline (UNCHANGED from 19.15)
 *
 * DSP-specific caveats:
 *   - **Endpoint divergence:** `/accounts/{dspAccountId}/dsp/reports/` (NOT
 *     `/reporting/reports`).
 *   - **Content-type divergence:** `application/vnd.dspcreatereports.v3+json`
 *     on BOTH Content-Type AND Accept.
 *   - **Body shape divergence:** flat top-level `type` + `dimensions[]` +
 *     `metrics[]` + `format: "JSON"` — NO `configuration` sub-object.
 *   - **Status enum divergence:** `IN_PROGRESS` (pending) → `SUCCESS` (terminal).
 *   - **Response format divergence:** flat JSON array (NOT NDJSON, NOT gzipped).
 *   - **SSRF allowlist tightening:** `corvo-reports.s3*.amazonaws.com` only.
 *   - **Scoping:** `dspAccountId` is `accountInfo.id` of an agency-typed
 *     profile (NOT `profileId`) — most-cited DSP onboarding footgun per
 *     amzn/ads-advanced-tools-docs Discussion #15.
 */

import {
  adapterErr,
  adapterOk,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type OAuthCredentials,
} from "../adapter";
import {
  AMAZON_ADS_PENDING_STATUSES_BY_PRODUCT,
  AMAZON_ADS_TERMINAL_SUCCESS_STATUSES_BY_PRODUCT,
  buildAmazonAdsCacheKey,
  buildAmazonAdsReportRequest,
  computeAmazonAdsCacheTtl,
  computeRequestHash,
  detectColumnDrift,
  downloadAndParseDspReport,
  isSafeS3DownloadUrl,
  normalizeRequestForHash,
  validateCachePayload,
  type SpReportRecord,
} from "./reports";
import {
  buildDspColumnList,
  pickDspReportType,
  type DspReportTypeId,
} from "./dsp-column-map";
import {
  introspectAudienceSchema,
  loadIntrospectedAudienceSchema,
} from "./dsp-audience-introspection";
import {
  introspectAudioVideoSchema,
  loadIntrospectedAudioVideoSchema,
} from "./dsp-audio-video-introspection";
import {
  runAmazonAdsReportPipeline,
  type AmazonAdsKvApi,
  type AmazonAdsReportJobsApi,
} from "./shared/report-orchestration";
import {
  chunkAndAggregate,
  type ChunkResult,
} from "../async-chunking";
import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
} from "./oauth";
import type {
  AmazonAdsProfile,
  RegionalProfilesResult,
} from "./api";

/** Story 19.16 AC #4 — DSP supports up to 31 days per report (NOT 60 — corrects directive). */
export const AMAZON_ADS_DSP_MAX_DAYS_PER_REPORT = 31;

/**
 * Story 19.16.1 AC #12 — per-DSP-report-type max-days map. All 7 entries are
 * defensively set to 31 (matches DSP_CAMPAIGN). Amazon does not publish a
 * separate max for the new report types; runtime drift detection (400 +
 * `amazon_ads_column_drift_detected` log) catches any divergence in
 * production so we can tighten in a follow-up story.
 *
 * Q1 2026 unified-reporting beta exposes 15-month daily/weekly + 6-year
 * monthly/yearly historical data, but each individual report request retains
 * the per-report-type 31-day max — `chunkAndAggregate` chunks at 31-day
 * boundaries so a 15-month range = 14 chunks.
 */
export const AMAZON_ADS_DSP_MAX_DAYS_BY_REPORT_TYPE: Record<string, number> = {
  DSP_CAMPAIGN: 31,
  DSP_INVENTORY: 31,
  DSP_GEOGRAPHY: 31,
  DSP_PRODUCT: 31,
  DSP_AUDIENCE: 31,
  DSP_TECHNICAL: 31,
  DSP_AUDIO_VIDEO: 31,
};

/** Story 19.16 AC #8 — DSP has a 12h reporting delay. */
export const AMAZON_ADS_DSP_DATA_FRESHNESS_LAG_HOURS = 12;

/** Story 19.16 — wall-clock budget (carry-forward 19.14). */
export const AMAZON_ADS_DSP_REPORT_TIMEOUT_MS = 30_000;

export interface RunDspFetchMetricsInput {
  readonly ctx: AdapterContext;
  readonly credentials: OAuthCredentials;
  readonly dateRange: { start: Date; end: Date };
  readonly integration: {
    /**
     * For DSP this is the `dspAccountId` (= `accountInfo.id` of an
     * agency-typed profile). The 19.13 OAuth callback enrichment
     * (Story 19.16 AC #9) persists this to `account_metadata.dspAdvertisers`;
     * the adapter passes it here as `accountId`.
     */
    readonly accountId: string;
    readonly region: AmazonAdsRegion;
    /**
     * Optional advertiser IDs to scope the request. Populated when the
     * dspAccountId is agency-typed (entity-scoped); omitted for
     * advertiser-scoped accounts (sending advertiserIds on advertiser-scoped
     * accounts returns 406 per Discussion #15).
     */
    readonly advertiserIds?: ReadonlyArray<string>;
  };
  readonly lwaClientId: string;
  readonly metrics?: ReadonlyArray<string>;
  readonly dimensions?: ReadonlyArray<string>;
  readonly jobs: AmazonAdsReportJobsApi;
  readonly kv: AmazonAdsKvApi;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly correlationId?: string;
  readonly newJobId?: () => string;
}

const DEFAULT_DSP_METRIC_KEYS = [
  "impressions",
  "viewableImpressions",
  "viewabilityRate",
  "clickThroughs",
  "CTR",
  "totalCost",
  "eCPM",
  "totalPurchases14d",
  "totalSales14d",
  "totalROAS14d",
  "newToBrandPurchases14d",
] as const;

/**
 * Story 19.16.1 — per-DSP-report-type default metric set when caller omits
 * `metrics`. AUDIENCE + AUDIO_VIDEO use the runtime-introspection floor;
 * TECHNICAL uses its 5 stable metrics; CAMPAIGN/INVENTORY/GEOGRAPHY/PRODUCT
 * inherit from `DEFAULT_DSP_METRIC_KEYS`.
 */
const DEFAULT_DSP_METRICS_BY_REPORT_TYPE: Record<
  DspReportTypeId,
  ReadonlyArray<string>
> = {
  DSP_CAMPAIGN: DEFAULT_DSP_METRIC_KEYS,
  DSP_INVENTORY: DEFAULT_DSP_METRIC_KEYS,
  DSP_GEOGRAPHY: DEFAULT_DSP_METRIC_KEYS,
  DSP_PRODUCT: DEFAULT_DSP_METRIC_KEYS,
  DSP_AUDIENCE: [
    "spend",
    "impressions",
    "clicks",
    "brandedSearches",
    "detailPageViews",
    "addToCart",
    "purchases",
  ],
  DSP_TECHNICAL: [
    "technicalImpressions",
    "technicalClicks",
    "technicalCost",
    "technicalPurchases14d",
    "technicalSales14d",
  ],
  DSP_AUDIO_VIDEO: [
    "audioImpressions",
    "audioCompleteListens",
    "videoCTVImpressions",
    "videoCTVCompletes",
  ],
};

export async function runDspFetchMetrics(
  input: RunDspFetchMetricsInput
): Promise<AdapterResult<AdapterMetrics>> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const now = input.now ?? Date.now;
  const sleep =
    input.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const correlationId = input.correlationId ?? crypto.randomUUID();
  const newJobId = input.newJobId ?? (() => crypto.randomUUID());

  const validation = validateRequest(input);
  if (!validation.ok) return adapterErr(validation.error);

  if (!AMAZON_ADS_REGIONAL_HOSTS[input.integration.region]) {
    return adapterErr({
      code: "invalid_response",
      message: `Unknown Amazon Ads region: ${input.integration.region}`,
      retryable: false,
    });
  }

  const dimensions = input.dimensions ?? [];
  const reportType = pickDspReportType(dimensions);
  if (!reportType.ok) {
    return adapterErr({
      code:
        reportType.error === "ad_product_dimension_mismatch"
          ? "invalid_request"
          : "invalid_response",
      message: reportType.message,
      retryable: false,
    });
  }

  const totalDays = daysInclusive(input.dateRange.start, input.dateRange.end);
  const allRecords: SpReportRecord[] = [];
  const allWarnings: string[] = [];

  if (totalDays > AMAZON_ADS_DSP_MAX_DAYS_PER_REPORT) {
    const chunkResult = await chunkAndAggregate<SpReportRecord>(
      { startDate: input.dateRange.start, endDate: input.dateRange.end },
      {
        maxDaysPerChunk: AMAZON_ADS_DSP_MAX_DAYS_PER_REPORT,
        timeBudgetMs: AMAZON_ADS_DSP_REPORT_TIMEOUT_MS,
      },
      (chunk) => {
        // Story 19.16 AC #16 — chunk-level forensic log.
        console.log(
          JSON.stringify({
            event: "amazon_ads_dsp_chunk_emitted",
            adProduct: "DSP",
            daysInChunk: daysInclusive(chunk.startDate, chunk.endDate),
            timestamp: new Date().toISOString(),
          })
        );
        return runOneDspChunk({
          input,
          fetchImpl,
          now,
          sleep,
          correlationId,
          newJobId,
          reportTypeId: reportType.reportTypeId,
          chunk,
        });
      },
      now
    );

    if (!chunkResult.success) {
      return adapterErr({
        code: (chunkResult.errorCode as never) ?? "invalid_response",
        message: chunkResult.errorMessage,
        retryable: false,
      });
    }
    if (chunkResult.partial) {
      allWarnings.push(
        `report_chunks_pending: ${chunkResult.chunksCompleted}/${chunkResult.chunksTotal} chunks completed`
      );
    }
    for (const r of chunkResult.records) allRecords.push(r);
    for (const w of chunkResult.warnings) allWarnings.push(w);
  } else {
    const r = await runOneDspChunk({
      input,
      fetchImpl,
      now,
      sleep,
      correlationId,
      newJobId,
      reportTypeId: reportType.reportTypeId,
      chunk: input.dateRange,
    });
    if (r.kind === "error" && r.fatal) {
      return adapterErr({
        code: (r.code as never) ?? "invalid_response",
        message: r.message,
        retryable: false,
      });
    }
    if (r.kind === "ok") {
      for (const rec of r.records) allRecords.push(rec);
      if (r.warnings) for (const w of r.warnings) allWarnings.push(w);
    } else {
      allWarnings.push(`single-chunk non-fatal: ${r.message}`);
    }
  }

  // DSP records pass through unchanged. parseDspJsonRecord already applied
  // Number.isFinite() guards (Round 3 Vuln 3 carry-forward).
  const finalRecords = allRecords;

  return adapterOk({
    platform: "amazon-ads",
    accountId: input.integration.accountId,
    dateRange: input.dateRange,
    records: finalRecords,
    fetchedAt: new Date(now()),
    recordCount: finalRecords.length,
    warnings: allWarnings.map((reason) => ({
      reportType: reportType.reportTypeId,
      reason,
    })),
    metadata: {
      dataFreshnessLagHours: AMAZON_ADS_DSP_DATA_FRESHNESS_LAG_HOURS,
      correlationId,
      adProduct: "DSP",
    },
  });
}

interface RunOneDspChunkArgs {
  readonly input: RunDspFetchMetricsInput;
  readonly fetchImpl: typeof fetch;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  readonly correlationId: string;
  readonly newJobId: () => string;
  readonly reportTypeId: DspReportTypeId;
  readonly chunk:
    | { start: Date; end: Date }
    | { startDate: Date; endDate: Date };
}

async function runOneDspChunk(
  args: RunOneDspChunkArgs
): Promise<ChunkResult<SpReportRecord>> {
  const start = "start" in args.chunk ? args.chunk.start : args.chunk.startDate;
  const end = "end" in args.chunk ? args.chunk.end : args.chunk.endDate;
  // Story 19.16.1 — per-report-type default metric set when caller omits
  // `metrics`. The default for AUDIENCE / TECHNICAL / AUDIO_VIDEO surfaces
  // matches Amazon's actual columns for those report types — the previous
  // single shared default leaked DSP_CAMPAIGN columns into AUDIENCE requests.
  let metrics: ReadonlyArray<string> =
    args.input.metrics ??
    DEFAULT_DSP_METRICS_BY_REPORT_TYPE[args.reportTypeId];
  // Story 19.16.1 AC #8 / AC #10 — for AUDIENCE / AUDIO_VIDEO, merge in any
  // previously-introspected schema columns from KV so subsequent fetches
  // request the full per-tenant column set (caller-supplied + introspected).
  // First-fetch path: KV read returns null → use floor only.
  if (
    args.reportTypeId === "DSP_AUDIENCE" ||
    args.reportTypeId === "DSP_AUDIO_VIDEO"
  ) {
    try {
      const cached =
        args.reportTypeId === "DSP_AUDIENCE"
          ? await loadIntrospectedAudienceSchema({
              kv: args.input.kv,
              tenantId: args.input.ctx.tenantId,
              integrationId: args.input.ctx.integrationId,
            })
          : await loadIntrospectedAudioVideoSchema({
              kv: args.input.kv,
              tenantId: args.input.ctx.tenantId,
              integrationId: args.input.ctx.integrationId,
            });
      if (cached && cached.length > 0) {
        const merged = new Set<string>([...metrics, ...cached]);
        metrics = [...merged];
      }
    } catch {
      // KV read failure is non-fatal — fall back to floor.
    }
  }
  const dimensions = args.input.dimensions ?? [];

  const built = buildAmazonAdsReportRequest({
    adProduct: "DSP",
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
    ...(args.input.integration.advertiserIds &&
    args.input.integration.advertiserIds.length > 0
      ? { advertiserIds: args.input.integration.advertiserIds }
      : {}),
  });
  const normalized = normalizeRequestForHash(built.body);
  const requestHash = await computeRequestHash(normalized);

  // Re-build is a no-op for DSP (DSP body has no `name` field that needs the
  // request-hash short suffix). We pass it through anyway for symmetry with
  // SP/SB/SD pipelines and forward-compat if Amazon adds a name field.
  const namedBuilt = built;

  const cacheKey = buildAmazonAdsCacheKey({
    adProduct: "DSP",
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    requestHash,
  });

  try {
    const cached = await args.input.kv.get(cacheKey);
    if (cached) {
      const parsed = safeJson(cached);
      if (parsed && Array.isArray(parsed.records)) {
        return {
          kind: "ok",
          records: parsed.records as SpReportRecord[],
          warnings: ["cache_hit", ...built.warnings],
        };
      }
    }
  } catch (err) {
    logEvent("amazon_ads_cache_read_failed", {
      correlationId: args.correlationId,
      adProduct: "DSP",
      message: err instanceof Error ? err.message : String(err),
    });
  }

  const result = await runAmazonAdsReportPipeline<SpReportRecord>({
    accessToken: args.input.credentials.accessToken,
    clientId: args.input.lwaClientId,
    profileId: args.input.integration.accountId,
    region: args.input.integration.region,
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    body: namedBuilt.body,
    requestHash,
    correlationId: args.correlationId,
    adProduct: "DSP",
    reportTypeId: args.reportTypeId,
    jobs: args.input.jobs,
    parseRecord: (r) => r,
    fetchImpl: args.fetchImpl,
    now: args.now,
    sleep: args.sleep,
    newJobId: args.newJobId,
    // Story 19.16 AC #11 — DSP-specific status overrides. Without these,
    // runAmazonAdsReportPipeline THROWS at orchestration entry (Round 4 QA
    // Gap 3 — kills the silent slow-rot failure mode of forgetting overrides).
    terminalSuccessStatuses: AMAZON_ADS_TERMINAL_SUCCESS_STATUSES_BY_PRODUCT.DSP,
    pendingStatuses: AMAZON_ADS_PENDING_STATUSES_BY_PRODUCT.DSP,
    // Story 19.16 Task 5.5 / AC #10 — DSP downloader (flat JSON, no gzip).
    downloadImpl: downloadAndParseDspReport,
    // Story 19.16 AC #10 — tighter SSRF allowlist (corvo-reports bucket).
    isSafeUrlImpl: (url) => isSafeS3DownloadUrl(url, "DSP"),
  });

  if (result.kind === "error") {
    return {
      kind: "error",
      message: result.message,
      code: result.code,
      fatal: !result.retryable,
    };
  }
  if (result.kind === "pending") {
    return {
      kind: "error",
      message: result.message,
      fatal: false,
    };
  }

  const records = result.records;
  const warnings: string[] = [...built.warnings, ...result.warnings];

  const requestedColumns = buildDspColumnList(
    args.reportTypeId,
    metrics,
    dimensions
  ).columns;
  const drift = detectColumnDrift(requestedColumns, records);
  if (drift.missingColumns.length > 0) {
    logEvent("amazon_ads_column_drift_detected", {
      correlationId: args.correlationId,
      adProduct: "DSP",
      reportId: result.reportId,
      missingColumns: drift.missingColumns,
    });
    for (const w of drift.warnings) warnings.push(w);
  }

  // Story 19.16.1 AC #1 / Task 15 — runtime schema introspection for the 2
  // partially-undocumented DSP report types. After the first successful fetch
  // for this (tenant, integration, reportType), persist the actual column
  // names Amazon returned to KV with a 7-day TTL. Subsequent fetches read the
  // cached schema. The introspection helpers handle drift detection on TTL
  // re-introspection + XSS-style column-name regex defense.
  if (
    records.length > 0 &&
    (args.reportTypeId === "DSP_AUDIENCE" ||
      args.reportTypeId === "DSP_AUDIO_VIDEO")
  ) {
    const firstRecord = records[0];
    const observedColumns = firstRecord
      ? Object.keys(firstRecord as Record<string, unknown>)
      : [];
    try {
      if (args.reportTypeId === "DSP_AUDIENCE") {
        await introspectAudienceSchema({
          kv: args.input.kv,
          tenantId: args.input.ctx.tenantId,
          integrationId: args.input.ctx.integrationId,
          observedColumns,
        });
      } else {
        await introspectAudioVideoSchema({
          kv: args.input.kv,
          tenantId: args.input.ctx.tenantId,
          integrationId: args.input.ctx.integrationId,
          observedColumns,
        });
      }
    } catch (err) {
      // Introspection MUST NEVER fail the fetch path — log and continue. The
      // floor schema fallback covers the next fetch if introspection fails.
      logEvent("amazon_ads_dsp_introspection_persist_failed", {
        correlationId: args.correlationId,
        reportTypeId: args.reportTypeId,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // Story 19.16 AC #5 / Task 13.6 — wire-typo observability log. Fires on first
  // observation of `totalDetaiPageViewCPA14d` or `playerTrailersClicks14d` so
  // future maintainers see Amazon's wire format is intentionally typo-preserved.
  const wireTypoFields = [
    "totalDetaiPageViewCPA14d",
    "playerTrailersClicks14d",
  ];
  const observedTypos = new Set<string>();
  for (const r of records) {
    for (const t of wireTypoFields) {
      if (Object.prototype.hasOwnProperty.call(r, t)) {
        observedTypos.add(t);
      }
    }
  }
  for (const t of observedTypos) {
    logEvent("amazon_ads_dsp_typo_field_used", {
      correlationId: args.correlationId,
      field: t,
    });
  }

  const cacheValidation = validateCachePayload(records);
  if (cacheValidation.valid) {
    const ttlSec = computeAmazonAdsCacheTtl(
      { startDate: start, endDate: end },
      "DSP",
      new Date(args.now())
    );
    try {
      await args.input.kv.put(cacheKey, JSON.stringify({ records }), ttlSec);
    } catch (err) {
      logEvent("amazon_ads_cache_write_failed", {
        correlationId: args.correlationId,
        adProduct: "DSP",
        reportId: result.reportId,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  } else {
    logEvent("amazon_ads_cache_validation_failed", {
      correlationId: args.correlationId,
      adProduct: "DSP",
      reportId: result.reportId,
      reason: cacheValidation.reason,
    });
  }

  return { kind: "ok", records, warnings };
}

// ============================================================================
// Story 19.16 Task 8.7 / AC #3 — pickDspAccountId
// ============================================================================

export type PickDspAccountIdResult =
  | {
      readonly ok: true;
      readonly dspAccountId: string;
      readonly advertiserIds: ReadonlyArray<string>;
      readonly region: AmazonAdsRegion;
    }
  | {
      readonly ok: false;
      readonly reason: "dsp_not_enabled" | "dsp_unauthorized" | "dsp_outage";
    };

/**
 * Story 19.16 AC #3 — pure helper that selects the DSP `accountId` path
 * parameter from a multi-region profile-discovery result.
 *
 * Selection rules (deterministic for stable behavior across re-auths):
 *   1. Filter for profiles where `accountInfo.type === "agency"` — entity-
 *      level access is the documented DSP scoping mechanism per Discussion #15.
 *   2. If no agency profile exists in any region, return `dsp_not_enabled`
 *      (customer's LWA app is connected but their Amazon account is NOT
 *      DSP-enabled — DSP is sales-rep-gated).
 *   3. If agency profile exists BUT the region returned `regionStatus ===
 *      "unauthorized"`, return `dsp_unauthorized`.
 *   4. If agency profile exists BUT the region returned `regionStatus ===
 *      "outage"`, return `dsp_outage` retryable.
 *   5. Multiple agency profiles → pick the FIRST in 19.13's region-priority
 *      order (NA > EU > FE).
 *
 * This helper is PURE — no IO. Caller passes in the already-fetched
 * `RegionalProfilesResult[]` from `fetchAmazonAdsProfilesAcrossRegions`.
 */
export function pickDspAccountId(
  profilesAcrossRegions: ReadonlyArray<{
    readonly region: AmazonAdsRegion;
    readonly result: RegionalProfilesResult;
  }>,
  /** Optional pre-fetched advertiserIds from `account_metadata.dspAdvertisers`. */
  advertiserIdsByDspAccountId?: ReadonlyMap<string, ReadonlyArray<string>>
): PickDspAccountIdResult {
  const REGION_PRIORITY: AmazonAdsRegion[] = ["NA", "EU", "FE"];
  // Walk in priority order so multi-region tenancies pick NA first.
  // Track agency profiles per region for fallthrough decisions.
  // _profile is captured for completeness even though we only use the
  // accountInfo.id below — keeping the structure flexible for future
  // forensic extensions (Round 5 First Principles).
  const agencyByRegion = new Map<
    AmazonAdsRegion,
    {
      readonly status: "ok" | "unauthorized" | "outage";
      readonly profile?: AmazonAdsProfile;
    }
  >();

  for (const { region, result } of profilesAcrossRegions) {
    if (result.regionStatus === "ok") {
      const agency = result.profiles.find(
        (p) => p.accountInfo.type === "agency"
      );
      if (agency) {
        agencyByRegion.set(region, { status: "ok", profile: agency });
      }
    } else if (result.regionStatus === "unauthorized") {
      agencyByRegion.set(region, { status: "unauthorized" });
    } else {
      agencyByRegion.set(region, { status: "outage" });
    }
  }

  // Walk priority order — first OK agency wins.
  for (const region of REGION_PRIORITY) {
    const entry = agencyByRegion.get(region);
    if (entry?.status === "ok" && entry.profile) {
      const dspAccountId = entry.profile.accountInfo.id;
      const advertiserIds =
        advertiserIdsByDspAccountId?.get(dspAccountId) ?? [];
      return {
        ok: true,
        dspAccountId,
        advertiserIds,
        region,
      };
    }
  }

  // No "ok" agency found — promote the most actionable failure reason.
  // Priority: unauthorized > outage > not_enabled (no_agency_profile).
  for (const region of REGION_PRIORITY) {
    const entry = agencyByRegion.get(region);
    if (entry?.status === "unauthorized") {
      console.log(
        JSON.stringify({
          event: "amazon_ads_dsp_not_enabled",
          regions: REGION_PRIORITY,
          reason: "all_regions_unauthorized",
          timestamp: new Date().toISOString(),
        })
      );
      return { ok: false, reason: "dsp_unauthorized" };
    }
  }
  for (const region of REGION_PRIORITY) {
    const entry = agencyByRegion.get(region);
    if (entry?.status === "outage") {
      console.log(
        JSON.stringify({
          event: "amazon_ads_dsp_not_enabled",
          regions: REGION_PRIORITY,
          reason: "all_regions_outage",
          timestamp: new Date().toISOString(),
        })
      );
      return { ok: false, reason: "dsp_outage" };
    }
  }

  console.log(
    JSON.stringify({
      event: "amazon_ads_dsp_not_enabled",
      regions: REGION_PRIORITY,
      reason: "no_agency_profile",
      timestamp: new Date().toISOString(),
    })
  );
  return { ok: false, reason: "dsp_not_enabled" };
}

// ============================================================================
// Validation + helpers
// ============================================================================

function validateRequest(
  input: RunDspFetchMetricsInput
):
  | { ok: true }
  | {
      ok: false;
      error: { code: "invalid_response"; message: string; retryable: false };
    } {
  if (
    !(input.dateRange.start instanceof Date) ||
    !(input.dateRange.end instanceof Date) ||
    Number.isNaN(input.dateRange.start.getTime()) ||
    Number.isNaN(input.dateRange.end.getTime())
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads DSP fetchMetrics: dateRange contains invalid Date",
        retryable: false,
      },
    };
  }
  if (input.dateRange.start.getTime() > input.dateRange.end.getTime()) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads DSP fetchMetrics: dateRange.start > dateRange.end",
        retryable: false,
      },
    };
  }
  if (
    !input.integration.accountId ||
    typeof input.integration.accountId !== "string"
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads DSP fetchMetrics: integration.accountId (dspAccountId from agency profile) is required",
        retryable: false,
      },
    };
  }
  if (!input.lwaClientId) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads DSP fetchMetrics: AMAZON_ADS_CLIENT_ID env binding missing",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.tenantId !== "string" ||
    input.ctx.tenantId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads DSP fetchMetrics: ctx.tenantId is required (cross-tenant cache isolation, Round 3 Vuln 4)",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.integrationId !== "string" ||
    input.ctx.integrationId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads DSP fetchMetrics: ctx.integrationId is required",
        retryable: false,
      },
    };
  }
  return { ok: true };
}

function daysInclusive(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

function safeJson(s: string): { records?: unknown } | null {
  try {
    return JSON.parse(s) as { records?: unknown };
  } catch {
    return null;
  }
}

function logEvent(event: string, details: Record<string, unknown>): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
